// Single source of truth for the auth session localStorage keys, shared by
// authContext.js, apiFetch.js, and any other module that needs to read/write
// the logged-in session.

export const TOKEN_KEY = "sibolab_auth_token";
export const REFRESH_TOKEN_KEY = "sibolab_refresh_token";
export const USER_KEY = "sibolab_auth_user";
export const ACTIVE_PORTAL_KEY = "sibolab_active_portal";
export const LANGUAGE_KEY = "sibolab_language";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function getUser() {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function saveSession(token, refreshToken, user) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

// Updates just the cached user object (e.g. after a profile refresh or a
// saved language change) without touching the token/refreshToken.
export function saveUser(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

// Fired on window by apiFetch whenever a token refresh brings back the user
// (detail = user payload) — roles may have changed since login, and
// AuthProvider applies the new ones to the running app.
export const SESSION_UPDATED_EVENT = "sibolab:session-updated";

const LOGOUT_REASON_KEY = "sibolab_logout_reason";

// One-shot "why you were signed out" marker, set right before a forced
// redirect to /login (a full page load, so React state can't carry it) and
// shown once by the login page. Currently only "inactive".
export function markLogoutReason(reason) {
  sessionStorage.setItem(LOGOUT_REASON_KEY, reason);
}

export function consumeLogoutReason() {
  const value = sessionStorage.getItem(LOGOUT_REASON_KEY);
  if (value) sessionStorage.removeItem(LOGOUT_REASON_KEY);
  return value;
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(ACTIVE_PORTAL_KEY);
}

const JUST_LOGGED_IN_KEY = "sibolab_just_logged_in";

// One-shot marker consumed by the "dynamic island" role indicator so it
// shows once right after a fresh session is established (password login or
// a register/reset flow's auto-login) but not on every page refresh of an
// already-persisted session. sessionStorage (not localStorage) so it also
// doesn't survive a real browser close.
export function markJustLoggedIn() {
  sessionStorage.setItem(JUST_LOGGED_IN_KEY, "1");
}

export function consumeJustLoggedIn() {
  const value = sessionStorage.getItem(JUST_LOGGED_IN_KEY);
  if (value) sessionStorage.removeItem(JUST_LOGGED_IN_KEY);
  return Boolean(value);
}

const TWO_FACTOR_PROMPT_KEY = "sibolab_two_factor_prompt";

// Separate one-shot marker for the post-login "do you want the email code?"
// prompt (TwoFactorChoiceDialog) - can't reuse the just-logged-in marker
// above, RoleSwitchIndicator already consumes that one. Set on every fresh
// session; the dialog only actually opens if the user hasn't chosen yet
// (user.twoFactorEnabled === null), so an undecided user sees it once per
// login until they pick.
export function markTwoFactorPrompt() {
  sessionStorage.setItem(TWO_FACTOR_PROMPT_KEY, "1");
}

export function consumeTwoFactorPrompt() {
  const value = sessionStorage.getItem(TWO_FACTOR_PROMPT_KEY);
  if (value) sessionStorage.removeItem(TWO_FACTOR_PROMPT_KEY);
  return Boolean(value);
}
